import { Checkbox } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo, useRef } from "react"

import { PurchaseOrderNumberCell } from "@/components/table"
import { SELECT_COLUMN_SIZE } from "@/constants/common"
import { PermissionActionType } from "@/constants/permissions"
import type { GoodsReceiptPallet, WarehouseArea } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import {
  GoodsReceiptPalletActionsCell,
  GoodsReceiptPalletAs400SyncStatusCell,
  GoodsReceiptPalletBatchNumbersCell,
  GoodsReceiptPalletCheckboxCell,
  GoodsReceiptPalletCurrentLocationCell,
  GoodsReceiptPalletDestinationCell,
  GoodsReceiptPalletLotNumbersCell,
  GoodsReceiptPalletLpnCell,
  GoodsReceiptPalletProductQtyCell,
  GoodsReceiptPalletReferenceIdCell,
} from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletsTable/cells"

interface UseCheckedInBuiltPalletsColumnsParams {
  warehouse?: WarehouseArea | null
  isAkrochemManaged?: boolean
  isSyncing?: boolean
  canMaintain?: boolean
  openActionsMenuId: string | null
  onActionsMenuOpen: (palletId: string) => void
  onActionsMenuClose: () => void
  onDestinationOverride?: (pallet: GoodsReceiptPallet) => void
  onLpnEdit?: (pallet: GoodsReceiptPallet) => void
  onQrPrint?: (pallet: GoodsReceiptPallet) => void
  onDelete?: (pallet: GoodsReceiptPallet) => void
}

export const useCheckedInBuiltPalletsColumns = ({
  warehouse,
  isAkrochemManaged = true,
  isSyncing = false,
  canMaintain = true,
  openActionsMenuId,
  onActionsMenuOpen,
  onActionsMenuClose,
  onDestinationOverride,
  onLpnEdit,
  onQrPrint,
  onDelete,
}: UseCheckedInBuiltPalletsColumnsParams): ColumnDef<GoodsReceiptPallet>[] => {
  const { hasPermissions } = usePermissionManager()

  // WORKAROUND: Prevent column definition recreation and subsequent cell remounting during polling.
  // Use a ref to read the latest menu state inside the cell renderer without breaking the useMemo cache.
  const openActionsMenuIdRef = useRef(openActionsMenuId)
  openActionsMenuIdRef.current = openActionsMenuId

  const actionsColumn = useMemo<ColumnDef<GoodsReceiptPallet>>(
    () => ({
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <GoodsReceiptPalletActionsCell
          pallet={row.original}
          shouldShowQrPrint={isAkrochemManaged}
          shouldShowDelete={canMaintain}
          isDeleteDisabled={row.original.isStored || isSyncing}
          menuOpen={openActionsMenuIdRef.current === row.original.goodsReceiptPalletId}
          onMenuOpen={onActionsMenuOpen}
          onMenuClose={onActionsMenuClose}
          onQrPrint={() => onQrPrint?.(row.original)}
          onDelete={() => onDelete?.(row.original)}
        />
      ),
      size: 100,
      enableSorting: false,
    }),
    [isAkrochemManaged, isSyncing, canMaintain, onActionsMenuOpen, onActionsMenuClose, onQrPrint, onDelete]
  )

  return useMemo<ColumnDef<GoodsReceiptPallet>[]>(() => {
    const checkboxColumn: ColumnDef<GoodsReceiptPallet> = {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
          data-testid='pallet-header-checkbox'
        />
      ),
      cell: ({ row }) => <GoodsReceiptPalletCheckboxCell row={row} />,
      size: SELECT_COLUMN_SIZE,
      enableSorting: false,
    }

    const coreColumns: ColumnDef<GoodsReceiptPallet>[] = [
      {
        accessorKey: "orderId",
        header: "PO #",
        enableSorting: false,
        cell: ({ row }) => {
          const order = row.original.goodsReceiptPalletRow?.order
          return order ? <PurchaseOrderNumberCell order={order} /> : null
        },
      },
      {
        accessorKey: "palletQty",
        header: "Product / Qty",
        cell: ({ row }) => <GoodsReceiptPalletProductQtyCell pallet={row.original} />,
      },
      {
        accessorKey: "lots",
        header: "Lot #",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletLotNumbersCell pallet={row.original} />,
      },
      {
        accessorKey: "batchNumber",
        header: "Batch #",
        enableSorting: false,
        cell: ({ row }) => (
          <GoodsReceiptPalletBatchNumbersCell goodsReceiptPalletRow={row.original.goodsReceiptPalletRow!} />
        ),
      },
      {
        accessorKey: "currentLocation",
        header: "Current Location",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletCurrentLocationCell pallet={row.original} />,
      },
      {
        accessorKey: "suggestedDestinationWarehouseShelving",
        header: "Destination",
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.isStored) {
            return null
          }

          const shouldShowEdit = hasPermissions([PermissionActionType.LOCATION_OVERRIDE]) && !!warehouse

          return (
            <GoodsReceiptPalletDestinationCell
              pallet={row.original}
              destination={row.original.destinationWarehouseShelving!}
              shouldShowEdit={shouldShowEdit}
              isEditDisabled={isSyncing}
              onOverride={() => onDestinationOverride?.(row.original)}
            />
          )
        },
      },
      {
        accessorKey: "referenceId",
        header: "Reference ID",
        cell: ({ row }) => <GoodsReceiptPalletReferenceIdCell pallet={row.original} />,
      },
      {
        accessorKey: "as400SyncStatus",
        header: "",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletAs400SyncStatusCell pallet={row.original} />,
      },
    ]

    const lpnColumn: ColumnDef<GoodsReceiptPallet> = {
      accessorKey: "lpn",
      header: "LPN",
      cell: ({ row }) => (
        <GoodsReceiptPalletLpnCell
          pallet={row.original}
          canMaintain={canMaintain}
          isEditDisabled={isSyncing}
          onEdit={() => onLpnEdit?.(row.original)}
        />
      ),
    }

    return [
      ...(isAkrochemManaged && canMaintain ? [checkboxColumn] : []),
      ...coreColumns,
      ...(isAkrochemManaged ? [] : [lpnColumn]),
      ...(isAkrochemManaged || canMaintain ? [actionsColumn] : []),
    ]
  }, [
    warehouse,
    isAkrochemManaged,
    isSyncing,
    canMaintain,
    hasPermissions,
    onDestinationOverride,
    onLpnEdit,
    actionsColumn,
  ])
}
